import React, { useMemo } from 'react';
import { TextLink } from '../../../../../design-system';
import { ALPHA_THEME } from '../../../../../design-system/ThemeProvider/alphaTheme';
import { useTranslation } from 'react-i18next';
import {
  PricingOptionsWrapper,
  ExpiryDetails,
  PaymentsTileContainer,
  ExpiryTextWrapper,
  OfferDescriptions,
  OfferDetailsCTAWrapper,
} from './PricingOptions.styles';
import {
  CopyTwo,
  TextCTA,
  StyledText,
  TextAlign,
  FlexContainer,
} from '../../../Common/Common.styles';
import { COLORS } from '../../../../styles/variables';
import PaymentsOptions from './PaymentsOptions';
import { PromotionType } from '../../../../types/offers';

type PricingOptionsProps = {
  offer: PromotionType;
  offerType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showOfferDetails: (e: any) => void;
};

const PricingOptions: React.FC<PricingOptionsProps> = ({ offer, offerType, showOfferDetails }) => {
  const { t } = useTranslation();
  const isAlpha = !process.env.IS_LEGACY;

  const { programShortDescription } = offer;

  const renderExpiry = useMemo(() => {
    return (
      <ExpiryDetails marginTop={offerType === 'finance' ? '20' : undefined}>
        <ExpiryTextWrapper>
          <StyledText
            data-testid="model-offer-valid-date"
            variant={{
              type: 'copy',
              order: '2',
              style: 'normal',
            }}
            color={isAlpha ? ALPHA_THEME.textCopyDefault : COLORS.grey60}
          >
            {t('promo.tile.offerEnd')} {offer.programToDate}
          </StyledText>
        </ExpiryTextWrapper>
        {isAlpha ? (
          <OfferDetailsCTAWrapper>
            <TextLink
              variant="standalone"
              size="sm"
              type="button"
              onClick={showOfferDetails}
              data-testid="model-cta-offer-details"
            >
              *{t('promo.tile.viewDetails')}
            </TextLink>
          </OfferDetailsCTAWrapper>
        ) : (
          <TextCTA
            data-testid="model-cta-offer-details"
            onClick={showOfferDetails}
            color={COLORS.grey60}
            size="small"
            isBold
          >
            *View offer details
          </TextCTA>
        )}
      </ExpiryDetails>
    );
  }, [offer.programToDate, offerType, showOfferDetails, t]);

  const renderPaymentAndLegal = useMemo(() => {
    return (
      <>
        <PaymentsTileContainer>
          <PaymentsOptions offer={offer} />
        </PaymentsTileContainer>
        <OfferDescriptions>
          <TextAlign>
            <CopyTwo>{programShortDescription}</CopyTwo>
          </TextAlign>
        </OfferDescriptions>
      </>
    );
  }, [offer, programShortDescription]);

  return (
    <PricingOptionsWrapper>
      {offerType === 'finance' ? (
        <FlexContainer direction="column" justifyContent="space-between" gap={12}>
          {renderPaymentAndLegal}
        </FlexContainer>
      ) : (
        renderPaymentAndLegal
      )}
      {renderExpiry}
    </PricingOptionsWrapper>
  );
};

export default PricingOptions;
